import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { useCart } from "../context/CartContext.jsx";
import AuthLayout from "../components/AuthLayout.jsx";
import { FarmLoaderOverlay } from "../components/FarmLoader.jsx";
import Seo from "../components/Seo.jsx";
import { sanitizePhone, isValidPhone } from "../utils/phone.js";
import { LAUNCH_PLAN_ID, LAUNCH_DATE } from "../data/products.js";

export default function Signup() {
  const { signup, requestSignupOtp } = useAuth();
  const { add, items } = useCart();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("details"); // "details" | "verify"
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [ttlMinutes, setTtlMinutes] = useState(10);
  const [busy, setBusy] = useState(false);

  const onChange = (e) => {
    const value = e.target.name === "phone" ? sanitizePhone(e.target.value) : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  };

  // Step 1 — capture details, then email an OTP.
  const requestCode = async (e) => {
    e.preventDefault();
    if (!isValidPhone(form.phone)) {
      setError("Enter a valid 10-digit Indian mobile number (starts with 6-9).");
      return;
    }
    setError("");
    setInfo("");
    setBusy(true);
    const res = await requestSignupOtp(form.email.trim().toLowerCase());
    setBusy(false);
    if (res.ok) {
      setTtlMinutes(res.ttlMinutes || 10);
      setStep("verify");
    } else {
      setError(res.error);
    }
  };

  // Step 2 — verify the emailed OTP and create the account in one go. The
  // OTP already proves email ownership, so there's no password step: the
  // account logs straight in and future sign-ins use a fresh emailed code.
  const verifyCode = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp.trim())) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError("");
    setBusy(true);
    const res = await signup({ ...form, otp: otp.trim() });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    // New accounts join with the ₹9 founding-member plan already in their
    // cart while the pre-launch offer is still running — the same "12 free
    // eggs" this page pitches above is delivered by that plan, so completing
    // it is what actually claims the gift, not the signup step alone. Anyone
    // who arrived via a join CTA (welcome popup, launch band) already has it
    // in their cart, so don't add a second one.
    if (Date.now() < LAUNCH_DATE.getTime()) {
      if (!items[LAUNCH_PLAN_ID]) add(LAUNCH_PLAN_ID);
      navigate(searchParams.get("next") || "/checkout");
    } else {
      navigate(searchParams.get("next") || "/account");
    }
  };

  const resendCode = async () => {
    setError("");
    setInfo("");
    setBusy(true);
    const res = await requestSignupOtp(form.email.trim().toLowerCase());
    setBusy(false);
    if (res.ok) setInfo(`A new code was sent to ${form.email}.`);
    else setError(res.error);
  };

  const titles = {
    details: "Create your account",
    verify: "Verify your email",
  };
  const subtitles = {
    details: "Sign up and we'll take you straight to claiming 12 free eggs for just ₹9.",
    verify: `Enter the 6-digit code we emailed to ${form.email || "your inbox"}. It expires in ${ttlMinutes} minutes.`,
  };

  return (
    <AuthLayout
      eyebrow="Join Rimmon Farm"
      title={titles[step]}
      subtitle={subtitles[step]}
      aside={{
        heading: "🎁 12 free eggs for ₹9",
        perks: [
          "12 pasture eggs FREE with your first delivery",
          "Free home delivery for 30 days",
          "Saved addresses for faster checkout",
          "Track every order in your account",
        ],
      }}
    >
      <Seo title="Create account" path="/signup" noindex />
      <FarmLoaderOverlay
        show={busy}
        message={step === "verify" ? "Verifying & creating account…" : "Sending code…"}
      />

      {step === "details" && (
        <form className="auth__form" onSubmit={requestCode}>
          {error && <p className="auth__error">{error}</p>}
          <label>
            Full name
            <input name="name" value={form.name} onChange={onChange} required placeholder="Ravi Kumar" />
          </label>
          <label>
            Email
            <input type="email" name="email" value={form.email} onChange={onChange} required placeholder="you@example.com" />
          </label>
          <label>
            WhatsApp number
            <input type="tel" name="phone" value={form.phone} onChange={onChange} required inputMode="numeric" maxLength={10} pattern="[6-9][0-9]{9}" title="10-digit Indian mobile number starting with 6-9" placeholder="9876543210" />
          </label>
          <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
            {busy ? "Sending code…" : "Verify email & continue"}
          </button>
        </form>
      )}

      {step === "verify" && (
        <form className="auth__form" onSubmit={verifyCode}>
          {error && <p className="auth__error">{error}</p>}
          {info && <p className="auth__banner">{info}</p>}
          <label>
            Verification code
            <input
              name="otp"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              placeholder="6-digit code"
              maxLength={6}
            />
          </label>
          <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
            {busy ? "Creating account…" : "Verify & create account"}
          </button>
          <p className="auth__switch">
            Didn't get it?{" "}
            <button type="button" className="auth__linklike" onClick={resendCode} disabled={busy}>
              Resend code
            </button>
            {" · "}
            <button
              type="button"
              className="auth__linklike"
              onClick={() => { setStep("details"); setError(""); setInfo(""); }}
              disabled={busy}
            >
              Change details
            </button>
          </p>
        </form>
      )}

      <p className="auth__switch">
        Already have an account?{" "}
        <Link to={searchParams.get("next") ? `/login?next=${encodeURIComponent(searchParams.get("next"))}` : "/login"}>
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
